<template>
  <toolbar-container>
    <template #toolbar>
      <main-toolbar>
        <v-text-field
          v-model="searchQuery"
          class="mr-5"
          style="max-width: 240px; margin-top: 2px;"
          clearable
          prepend-icon="mdi-magnify"
          hide-details
        />
        <v-select
          v-model="categoryFilter"
          class="mr-5 mt-2"
          style="max-width: 200px"
          dense
          :label="$t('label.category')"
          :items="categories"
          hide-details
        />
        <v-checkbox
          v-model="showOnlyInstalled"
          class="ml-5"
          dense
          :label="$t('label.only_installed')"
          hide-details
        />
      </main-toolbar>
    </template>

    <v-container>
      <v-row>
        <v-col
          v-for="a in filteredApps"
          :key="a.id"
          cols="12"
          sm="6"
          lg="4"
          xl="3"
        >
          <app-card
            :ref="setAppCard"
            :app="a"
          />
        </v-col>
      </v-row>
    </v-container>
  </toolbar-container>
</template>

<script setup lang="ts">
import {
  computed, nextTick, onBeforeUpdate, onMounted, ref,
} from 'vue';
import AppCard from '@/components/views/settings/apps/AppCard.vue';
import MainToolbar from '@/components/views/main/MainToolbar.vue';
import store from '@/store/store';
import i18n from '@/plugins/i18n';
import { useRoute } from 'vue-router/composables';
import GstplnApp, { GstplnAppCategory } from '../apps/app';

const route = useRoute();

const categoryFilter = ref(route.query.category as string || '');
const categories = [
  { text: i18n.tc('label.show_all'), value: '' },
  { divider: true },
  { text: i18n.tc('code.app_category.extensions'), value: GstplnAppCategory.Extensions },
  { text: i18n.tc('code.app_category.booking_partners'), value: GstplnAppCategory.BookingPartners },
  { text: i18n.tc('code.app_category.integrations'), value: GstplnAppCategory.Integrations },
  { text: i18n.tc('code.app_category.payments'), value: GstplnAppCategory.Payments },
  { text: i18n.tc('code.app_category.pos'), value: GstplnAppCategory.Pos },
];

const showOnlyInstalled = ref(false);
const installedApps = computed(() => store.getters.gstplnApps.filter((a: GstplnApp) => a.installed));

const searchQuery = ref('');

const apps = computed(() => (showOnlyInstalled.value ? installedApps.value : store.getters.gstplnApps) as GstplnApp[]);
const filteredApps = computed(() => {
  const byCategory = categoryFilter.value === ''
    ? apps.value
    : apps.value.filter((a) => a.categories.includes(categoryFilter.value));

  const q = (searchQuery.value || '').trim().toLowerCase();
  if (!q) return byCategory;

  return byCategory.filter((a) => (a.title?.() || '').toLowerCase().includes(q));
});

// open config settings from route
const appCards = ref(new Map<string, InstanceType<typeof AppCard>>());
const setAppCard = (ac: InstanceType<typeof AppCard>) => appCards.value.set(ac.$props.app.apiName, ac);
onBeforeUpdate(() => {
  appCards.value = new Map<string, InstanceType<typeof AppCard>>();
});
onMounted(() => {
  // test app configure request
  const appName = route.query.configure as string;
  if (!appName) return;

  // wait until loaded
  const unwatch = store.watch((state, getters) => getters.isLoaded, async (newValue, oldValue) => {
    if (!newValue) return;
    const appCard = appCards.value.get(appName);
    console.log('appCard: ', appCard, 'appCards: ', appCards.value);
    appCard?.goToApp(true);
    await nextTick(); // for immedated unwatch is not yet set
    unwatch();
  }, { immediate: true });
});

</script>
